import { useEffect, useMemo, useState } from "react";
import { ExternalLink, FileText, Loader2 } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { WSFile } from "@/hooks/useBridgetteWorkspace";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

interface Props {
  file: WSFile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PdfPageImage {
  pageNumber: number;
  src: string;
}

const toAbsoluteStorageUrl = (url?: string | null) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const baseUrl = import.meta.env.VITE_SUPABASE_URL?.replace(/\/$/, "");
  return baseUrl ? `${baseUrl}/storage/v1${url.startsWith("/") ? url : `/${url}`}` : url;
};

const canvasToBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png");
  });

export function DocumentPreviewDialog({ file, open, onOpenChange }: Props) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pdfPages, setPdfPages] = useState<PdfPageImage[]>([]);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const isImage = useMemo(() => file?.file_type?.startsWith("image/"), [file]);
  const isPdf = useMemo(
    () => file?.file_type === "application/pdf" || file?.file_name.toLowerCase().endsWith(".pdf"),
    [file],
  );

  useEffect(() => {
    if (!open || !file) {
      setSignedUrl(null);
      setPreviewUrl(null);
      setPdfPages([]);
      setPreviewError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let imageObjectUrl: string | null = null;
    const pdfObjectUrls: string[] = [];

    const loadPreview = async () => {
      try {
        setLoading(true);
        setSignedUrl(null);
        setPreviewUrl(null);
        setPdfPages([]);
        setPreviewError(null);

        const { data, error } = await supabase.storage.from("bridgette-workspace").createSignedUrl(file.storage_path, 60 * 10);
        if (error) throw error;

        const signed = data as { signedUrl?: string; signedURL?: string } | null;
        const absoluteUrl = toAbsoluteStorageUrl(signed?.signedUrl ?? signed?.signedURL);
        if (!absoluteUrl) throw new Error("Could not load document preview");
        if (cancelled) return;

        setSignedUrl(absoluteUrl);

        const response = await fetch(absoluteUrl);
        if (!response.ok) throw new Error("Could not load document preview");
        const blob = await response.blob();
        if (cancelled) return;

        if (isImage) {
          imageObjectUrl = URL.createObjectURL(blob);
          setPreviewUrl(imageObjectUrl);
          return;
        }

        if (!isPdf) {
          return;
        }

        const pdf = await pdfjsLib.getDocument({ data: await blob.arrayBuffer() }).promise;
        const renderedPages: PdfPageImage[] = [];

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          if (cancelled) return;

          const page = await pdf.getPage(pageNumber);
          const baseViewport = page.getViewport({ scale: 1 });
          const targetWidth = Math.min(baseViewport.width, 1200);
          const scale = targetWidth / baseViewport.width;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d", { alpha: false });

          if (!context) continue;

          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);

          await page.render({ canvas, canvasContext: context, viewport }).promise;

          const pageBlob = await canvasToBlob(canvas);
          if (!pageBlob) continue;

          const pageUrl = URL.createObjectURL(pageBlob);
          pdfObjectUrls.push(pageUrl);
          renderedPages.push({ pageNumber, src: pageUrl });

          if (!cancelled) {
            setPdfPages([...renderedPages]);
          }
        }
      } catch (error) {
        console.error("Document preview failed", error);
        if (!cancelled) {
          setPreviewUrl(null);
          setPdfPages([]);
          setPreviewError("Could not render the document preview here. Use Open to view the file.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadPreview();

    return () => {
      cancelled = true;
      if (imageObjectUrl) URL.revokeObjectURL(imageObjectUrl);
      pdfObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [file, isImage, isPdf, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[88vh] max-w-6xl flex-col p-0">
        <DialogHeader className="border-b border-border px-5 py-4">
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="truncate text-base">{file?.file_name ?? "Document preview"}</DialogTitle>
            <DialogDescription className="sr-only">Preview the selected workspace document.</DialogDescription>
            {signedUrl ? (
              <Button variant="outline" size="sm" asChild>
                <a href={signedUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" /> Open
                </a>
              </Button>
            ) : null}
          </div>
        </DialogHeader>

        <div className="relative flex min-h-0 flex-1 items-center justify-center bg-muted/20 p-4">
          {isPdf && !previewError ? (
            <div className="h-full w-full overflow-y-auto">
              <div className="mx-auto flex w-full max-w-5xl flex-col gap-4">
                {pdfPages.map((page) => (
                  <div key={page.pageNumber} className="overflow-hidden rounded-md border border-border bg-background shadow-sm">
                    <img
                      src={page.src}
                      alt={`${file?.file_name ?? "Document"} page ${page.pageNumber}`}
                      className="block h-auto w-full"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {!loading && previewUrl && isImage ? (
            <img src={previewUrl} alt={file?.file_name ?? "Uploaded document"} className="max-h-full max-w-full rounded-md object-contain" />
          ) : null}

          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-background/60">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin text-primary" /> Rendering document…
              </div>
            </div>
          ) : null}

          {!loading && previewError ? (
            <div className="text-center text-sm text-muted-foreground">
              <FileText className="mx-auto mb-3 h-8 w-8" />
              {previewError}
            </div>
          ) : null}

          {!loading && !previewError && !isPdf && (!previewUrl || !isImage) ? (
            <div className="text-center text-sm text-muted-foreground">
              <FileText className="mx-auto mb-3 h-8 w-8" />
              Preview is available for PDFs and images. Use Open to view this file.
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}